import { useMemo } from 'react';
import { IOrderItem as IOrder } from '@/types';
import { IOrderItem } from '../context';
import { getItemIsBillingCloud } from '../utils';

/**
 * Retrieves and flattens all order items from different categories within an order
 * Combines items, itemsToAddLicenses, itemsToRenewal, and itemsToUpgrade arrays
 *
 * @param currentOrder - The order object containing various item arrays
 * @returns Sorted array of all order items from all categories
 */
export const getAllOrderItems = (currentOrder: IOrder | null): IOrderItem[] => {
  if (!currentOrder) return [];

  const itemsArrays = [
    currentOrder.items as IOrderItem[],
    currentOrder.itemsToAddLicenses as IOrderItem[],
    currentOrder.itemsToRenewal as IOrderItem[],
    currentOrder.itemsToUpgrade as IOrderItem[],
  ].filter(Boolean);

  const allItems = itemsArrays.flat();

  return allItems.sort((a, b) => {
    const aId = getItemIdentifier(a);
    const bId = getItemIdentifier(b);
    return aId.localeCompare(bId);
  });
};

/**
 * Gets the unique identifier for an order item
 */
const getItemIdentifier = (item: any): string => {
  return item._id || item.product?._id || item.tempId || '';
};

/**
 * Custom hook to manage order items processing and filtering
 * @param currentOrder - The current order data
 * @returns Object containing processed order items
 */
export const useOrderItems = (currentOrder: IOrder | null) => {
  const allOrderItems = useMemo(() => {
    return getAllOrderItems(currentOrder);
  }, [currentOrder]);

  const nonBillingCloudItems = useMemo(() => {
    return allOrderItems.filter((item) => !getItemIsBillingCloud(item));
  }, [allOrderItems]);

  const billingCloudItems = useMemo(() => {
    return allOrderItems.filter((item) => getItemIsBillingCloud(item));
  }, [allOrderItems]);

  const orderItemsToBill = useMemo(() => {
    return allOrderItems.filter((item) => !getItemIsBillingCloud(item));
  }, [allOrderItems]);

  return {
    allOrderItems,
    nonBillingCloudItems,
    billingCloudItems,
    orderItemsToBill,
    totalItems: allOrderItems.length,
    hasItems: allOrderItems.length > 0,
  };
};
